import { useTenant } from "@/stores/tenantStore.tsx";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button.tsx";
import { useAuth } from "@/hooks/useAuth.ts";
import type { ApiError } from "@/api/client.ts";
import { SEOHead } from "@/components/seo/SEOHead.tsx";

export function SignupPage() {
  const { siteName } = useTenant();
  const { signup } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setValidationError(null);
    if (password.length < 8) { setValidationError("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { setValidationError("Passwords don't match"); return; }
    signup.mutate({ email, password });
  };

  const apiError = signup.error as ApiError | null;
  const error = validationError ?? apiError?.message ?? null;

  return (
    <>
      <SEOHead title="Sign up" description="Create a free TubeForge account." path="/signup" noindex={true} />
      <div className="lg:hidden mb-8 text-center">
        <span className="text-2xl font-bold text-surface-900">{siteName}</span>
      </div>

      <h1 className="text-2xl font-semibold text-surface-900 mb-1">Create an account</h1>
      <p className="text-[13px] text-surface-400 mb-6">Start making faceless videos in minutes</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" autoComplete="email" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                       placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters" autoComplete="new-password" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                       placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Confirm password</label>
          <input
            type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••" autoComplete="new-password" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                       placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
          />
        </div>
        {error && <p className="text-[12px] text-danger">{error}</p>}
        <Button type="submit" loading={signup.isPending} fullWidth>Create account</Button>
      </form>

      <p className="mt-6 text-center text-[13px] text-surface-400">
        Already have an account?{" "}
        <Link to="/login" className="text-brand-600 hover:underline font-medium">Log in</Link>
      </p>
    </>
  );
}
